// src/utils/canvasRenderer.js
//
// Draws all designer elements onto an HTML5 Canvas 2D context.
// Coordinates are in mm; renderer converts to px using zoom.
//
// PHASE 3 OF DATE UNIFICATION (2026-06-13):
//
// - All token-bearing elements (database, date, date_plus,
//   value_only) flow through drawTokenized which honours the
//   showLabel flag. The pre-Phase-3 split between drawCompoundText
//   (for date+database) and drawSimpleText's value_only branch is
//   gone — one path, simpler.
// - Date elements (incl. legacy date_plus / value_only-date) use
//   the unified renderDateValue resolver from dateFormatter.js,
//   which handles all four dateModes (print/produced/expiry/fixed).
// - showLabel=0 hides fieldLabel even when it's set. showLabel=1
//   shows it with labelSeparator + value. Legacy rows that don't
//   have showLabel populated derive it from "is fieldLabel empty"
//   for backwards compatibility.
//
// LOGO RESIZE (2026-06-14):
//
// - drawImage now prefers el.elementWidth/elementHeight over the
//   upload-time parsed.widthMm/heightMm. Previously the upload
//   dimensions won, which meant resize handles updated the element
//   bounds in Vuex but the rendered image stayed locked at upload
//   size — the bug looked like "the logo just moves, doesn't resize".
//   parsed.widthMm now only serves as fallback for legacy elements
//   where elementWidth was never populated.

import { mmToPx } from './unitConverter.js'
import { renderBarcode, renderQR } from './barcodeHelper.js'
import { renderDateValue } from './dateFormatter.js'
import { cssFamilyFor, DEFAULT_FONT_KEY } from './fontManifest.js'

// The company's primary font_key, for elements that carry none of their
// own. Set once by the designer via setDefaultFontKey(); falls back to
// the built-in family so a bare import still renders something sane.
//
// Module-level rather than a renderCanvas parameter because the three
// text paths below are private helpers several calls deep, and
// threading it through all of them would touch far more code than the
// value is worth — it changes at most once per session, when the font
// list loads.
let _defaultFontKey = DEFAULT_FONT_KEY

export function setDefaultFontKey(key) {
  _defaultFontKey = key || DEFAULT_FONT_KEY
}
import { drawNutrition, getNutritionBounds } from './nutritionRenderer.js'
import { API_ORIGIN } from '@/services/api.js'

const TEXT_WIDTH_CACHE = new Map()

export function clearTextWidthCache() {
  TEXT_WIDTH_CACHE.clear()
}

function n(v, fallback = 0) {
  const x = Number(v)
  return Number.isFinite(x) ? x : fallback
}

function resolveImageSrc(src) {
  if (!src || typeof src !== 'string') return src
  if (src.startsWith('data:'))      return src
  if (src.startsWith('http://'))    return src
  if (src.startsWith('https://'))   return src
  if (src.startsWith('/'))          return API_ORIGIN + src
  return src
}

const COLUMN_TO_TOKEN = {
  itemName:      'product_name',
  productNumber: 'product_number',
  barcode:       'ean_code',
  description:   'description',
  allergens:     'allergens',
  notes:         'notes',
}
function normalizeToken(token) {
  if (!token) return token
  return COLUMN_TO_TOKEN[token] || token
}

function transformAllergens(text, allergenCase, separator, locale) {
  if (text == null) return ''
  const str = String(text)
  if (!str || !allergenCase || allergenCase === 'keep') return str

  const loc = locale || 'en'
  if (allergenCase === 'lower') return str.toLocaleLowerCase(loc)
  if (allergenCase === 'upper') return str.toLocaleUpperCase(loc)

  if (allergenCase === 'title') {
    const sep = separator || ', '
    return str.split(sep).map(piece => {
      const trimmed = piece.trimStart()
      const lead    = piece.slice(0, piece.length - trimmed.length)
      if (!trimmed) return piece
      const first = trimmed[0].toLocaleUpperCase(loc)
      return lead + first + trimmed.slice(1)
    }).join(sep)
  }

  return str
}

const TWO_D_SYMBOLOGIES = new Set(['qrcode', 'datamatrix'])

const DATE_FIELD_TYPES = new Set(['date', 'date_plus'])

function isBarcodeElement(el) {
  return el.fieldType === 'barcode' || el.fieldType === 'qrcode'
}

function isDateElement(el) {
  if (DATE_FIELD_TYPES.has(el.fieldType)) return true
  if (el.fieldType === 'value_only'
      && (el.valueOnlyType === 'date' || el.valueOnlyType === 'date_plus')) {
    return true
  }
  return false
}

function isTokenizedElement(el) {
  if (el.fieldType === 'database') return true
  if (isDateElement(el)) return true
  if (el.fieldType === 'value_only') return true
  return false
}

function shouldShowLabel(el) {
  if (el.showLabel === 1 || el.showLabel === true)  return true
  if (el.showLabel === 0 || el.showLabel === false) return false
  return !!(el.fieldLabel && String(el.fieldLabel).trim())
}

function symbologyOf(el) {
  if (el.fieldType === 'qrcode') return 'qrcode'
  return (el.barcodeType || 'code128').toLowerCase()
}

function getElementWidth(el, ctx, zoom) {
  if (isBarcodeElement(el)) {
    const sym = symbologyOf(el)
    if (TWO_D_SYMBOLOGIES.has(sym)) {
      return n(el.qrSize, n(el.elementWidth, 15))
    }
    return n(el.barcodeWidth, n(el.elementWidth, 30))
  }
  if (el.fieldType === 'divider') return n(el.lineWidth, 50)
  if (el.fieldType === 'logo' || el.fieldType === 'image') return n(el.elementWidth, 20)
  if (el.fieldType === 'nutrition') return n(el.elementWidth, 40)

  const idCached = el.tempId && TEXT_WIDTH_CACHE.get(`id|${el.tempId}`)
  if (idCached && idCached > 0) return idCached
  const ew = n(el.elementWidth, 0)
  if (ew > 0) return ew

  if (ctx) {
    const fsPx   = mmToPx(n(el.fontSize, 3)) * zoom
    const weight = el.fontBold   ? 'bold'   : 'normal'
    const style  = el.fontItalic ? 'italic' : 'normal'
    // Was `el.font_key || 'Arial, sans-serif'` — which put the raw
    // font_key straight into ctx.font as a CSS family name and fell
    // back to Arial. Two problems: the key is not always a valid family
    // (PPRightGrotesk-Medium is registered as PPRightGroteskMedium),
    // and Arial is not what the printer uses for an element with no
    // font, so the preview lied.
    const family = cssFamilyFor(el.font_key || _defaultFontKey)
    const text   = el.fieldLabel || el.databaseField || el.fieldType || ''
    const font   = `${style} ${weight} ${fsPx}px ${family}`
    const cacheKey = `${font}|${text}`

    if (TEXT_WIDTH_CACHE.has(cacheKey)) return TEXT_WIDTH_CACHE.get(cacheKey)

    ctx.save()
    ctx.font = font
    const w = ctx.measureText(text).width / zoom / mmToPx(1)
    ctx.restore()
    TEXT_WIDTH_CACHE.set(cacheKey, w)
    return w
  }

  return 20
}

function getElementHeight(el) {
  if (isBarcodeElement(el)) {
    const sym = symbologyOf(el)
    if (TWO_D_SYMBOLOGIES.has(sym)) {
      return n(el.qrSize, n(el.elementHeight, 15))
    }
    return n(el.barcodeHeight, n(el.elementHeight, 10))
  }
  if (el.fieldType === 'divider') return n(el.lineThickness, 0.5)
  if (el.fieldType === 'logo' || el.fieldType === 'image') return n(el.elementHeight, 10)
  if (el.fieldType === 'nutrition') {
    return getNutritionBounds(el).heightMm
  }
  if (el.wordWrap && n(el.elementWidth, 0) > 0) {
    const lines = n(el.maxLines, 2)
    const lineH = n(el.fontSize, 3) * 1.2
    return lines * lineH
  }
  return n(el.fontSize, 3)
}

function getEffectiveBounds(el, ctx, zoom) {
  const w   = getElementWidth(el, ctx, zoom)
  const h   = getElementHeight(el)
  const rot = n(el.rotation, 0)
  if (rot === 90 || rot === 270) return { w: h, h: w }
  return { w, h }
}

function resolveValueText(el, previewMode, previewData, locale) {
  if (isDateElement(el)) {
    return renderDateValue(el, previewMode ? previewData : {}, locale)
  }
  if (el.fieldType === 'database') {
    const token = normalizeToken(el.databaseField)
    if (!token) return ''
    if (previewMode) {
      let val = previewData[token] ?? `{${token}}`
      if (token === 'allergens') {
        val = transformAllergens(val, el.allergenCase, el.allergenSeparator, locale)
      }
      return val
    }
    return `{${token}}`
  }
  if (el.fieldType === 'value_only' && el.valueOnlyType === 'database') {
    const token = normalizeToken(el.databaseField)
    if (!token) return ''
    if (previewMode) {
      let val = previewData[token] ?? `{${token}}`
      if (token === 'allergens') {
        val = transformAllergens(val, el.allergenCase, el.allergenSeparator, locale)
      }
      return val
    }
    return `{${token}}`
  }
  return ''
}

export async function renderCanvas(ctx, {
  label, elements, zoom, selectedIds = [],
  previewMode = false, previewData = {},
  gridEnabled = true, gridSizeMm = 1,
  locale = 'en',
  t = null,
}) {
  const z = n(zoom, 1)
  const W = ctx.canvas.width
  const H = ctx.canvas.height

  if (!label) return
  if (W === 0 || H === 0) return

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, W, H)

  if (gridEnabled && gridSizeMm > 0) drawGrid(ctx, label, z, gridSizeMm)

  ctx.strokeStyle = label.labelColor || '#448AFF'
  ctx.lineWidth   = 1.5
  ctx.strokeRect(0.75, 0.75, W - 1.5, H - 1.5)

  const list   = Array.isArray(elements) ? elements : []
  const sorted = [...list].sort((a, b) => n(a.zIndex, 0) - n(b.zIndex, 0))

  for (const el of sorted) {
    try {
      await drawElement(ctx, el, z, previewMode, previewData, label, locale, t)
    } catch (err) {
      console.error('drawElement threw for', el.fieldType, el.tempId, err)
    }
  }

  for (const el of sorted) {
    if (selectedIds.includes(el.tempId)) drawSelectionHandles(ctx, el, z)
  }
}

function drawGrid(ctx, label, zoom, gridSizeMm) {
  const W      = mmToPx(n(label.labelWidth, 0))  * zoom
  const H      = mmToPx(n(label.labelHeight, 0)) * zoom
  const stepPx = mmToPx(gridSizeMm) * zoom
  if (stepPx < 2) return

  ctx.save()
  ctx.strokeStyle = 'rgba(100,100,200,0.15)'
  ctx.lineWidth   = 0.5
  for (let x = stepPx; x < W; x += stepPx) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
  }
  for (let y = stepPx; y < H; y += stepPx) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
  }
  ctx.restore()
}

async function drawElement(ctx, el, zoom, previewMode, previewData, label, locale, t) {
  const x   = mmToPx(n(el.labelX, 0)) * zoom
  const y   = mmToPx(n(el.labelY, 0)) * zoom
  const rot = n(el.rotation, 0)

  ctx.save()

  if (rot) {
    ctx.translate(x, y)
    ctx.rotate((rot * Math.PI) / 180)
    ctx.translate(-x, -y)
  }

  if (el.locked) ctx.globalAlpha = 0.85

  if (isBarcodeElement(el)) {
    const sym = symbologyOf(el)
    if (TWO_D_SYMBOLOGIES.has(sym)) {
      await drawQR(ctx, el, x, y, zoom, previewMode, previewData)
    } else {
      await drawBarcode(ctx, el, x, y, zoom, previewMode, previewData)
    }
    ctx.restore()
    return
  }

  if (isTokenizedElement(el)) {
    drawTokenized(ctx, el, x, y, zoom, previewMode, previewData, label, locale)
    ctx.restore()
    return
  }

  switch (el.fieldType) {
    case 'static':
    case 'description':
    case 'prompt':
    case 'allergens_text':
      drawSimpleText(ctx, el, x, y, zoom, previewMode, previewData, locale)
      break
    case 'logo':
    case 'image':
      await drawImage(ctx, el, x, y, zoom)
      break
    case 'divider':
      drawDivider(ctx, el, x, y, zoom)
      break
    case 'nutrition':
      drawNutrition(ctx, el, x, y, zoom, previewMode, previewData, locale, t)
      break
    default:
      drawPlaceholder(ctx, el, x, y, zoom)
  }

  ctx.restore()
}

function _wrapText(ctx, text, maxWidthPx) {
  const normalised = String(text).replace(/\r\n?/g, '\n')
  const hardLines  = normalised.split('\n')
  const lines = []
  for (const hard of hardLines) {
    if (hard === '') {
      lines.push('')
      continue
    }
    const words = hard.split(' ')
    let current = ''
    for (const word of words) {
      const test = current ? `${current} ${word}` : word
      if (ctx.measureText(test).width > maxWidthPx && current) {
        lines.push(current)
        current = word
      } else {
        current = test
      }
    }
    if (current) lines.push(current)
  }
  return lines
}

function _splitHardLines(text) {
  return String(text).replace(/\r\n?/g, '\n').split('\n')
}

function resolveTextAnchor(el, leftXpx, zoom) {
  const align   = el.textAlign || 'left'
  const widthMm = n(el.elementWidth, 0)
  const widthPx = mmToPx(widthMm) * zoom

  if (!widthPx || align === 'left') {
    return { anchorX: leftXpx, ctxAlign: 'left' }
  }
  if (align === 'center') {
    return { anchorX: leftXpx + widthPx / 2, ctxAlign: 'center' }
  }
  return { anchorX: leftXpx + widthPx, ctxAlign: 'right' }
}

function drawSimpleText(ctx, el, x, y, zoom, previewMode, previewData, locale) {
  const fsPx   = mmToPx(n(el.fontSize, 3)) * zoom
  const weight = el.fontBold   ? 'bold'   : 'normal'
  const style  = el.fontItalic ? 'italic' : 'normal'
  const family = cssFamilyFor(el.font_key || _defaultFontKey)

  let text
  if (el.fieldType === 'prompt') {
    const token = el.databaseField
    if (previewMode && token && previewData[token] != null && previewData[token] !== '') {
      text = String(previewData[token])
    } else {
      text = el.fieldLabel || (token ? `{${token}}` : '')
    }
  } else if (el.fieldType === 'static') {
    text = el.fieldLabel || ''
  } else if (el.fieldType === 'allergens_text') {
    const rawAllergens = previewMode ? (previewData.allergens || '') : ''
    const allergens    = transformAllergens(
      rawAllergens, el.allergenCase, el.allergenSeparator, locale
    )
    const showLabel = shouldShowLabel(el)
    if (previewMode) {
      const valueText = allergens || '{allergens}'
      text = (showLabel && el.fieldLabel)
        ? `${el.fieldLabel}: ${valueText}`
        : valueText
    } else {
      text = (showLabel && el.fieldLabel)
        ? `${el.fieldLabel}: {allergens}`
        : '{allergens}'
    }
  } else if (previewMode && el.databaseField) {
    const token = normalizeToken(el.databaseField)
    let val = previewData[token] ?? `{${token}}`
    if (token === 'allergens') {
      val = transformAllergens(val, el.allergenCase, el.allergenSeparator, locale)
    }
    text = val
  } else {
    const token = normalizeToken(el.databaseField)
    text = el.fieldLabel || (token ? `{${token}}` : '')
  }
  text = String(text || '')

  ctx.font         = `${style} ${weight} ${fsPx}px ${family}`
  ctx.fillStyle    = '#000000'
  ctx.textBaseline = 'top'

  if (el.wordWrap && n(el.elementWidth, 0) > 0) {
    const maxW     = mmToPx(n(el.elementWidth, 0)) * zoom
    const maxLines = n(el.maxLines, 99)
    const lines    = _wrapText(ctx, text, maxW)
    const limited  = lines.slice(0, maxLines)

    const { anchorX, ctxAlign } = resolveTextAnchor(el, x, zoom)
    ctx.textAlign = ctxAlign
    limited.forEach((line, i) => ctx.fillText(line, anchorX, y + i * fsPx * 1.2))

    if (el.tempId) TEXT_WIDTH_CACHE.set(`id|${el.tempId}`, n(el.elementWidth, 0))
    if (!previewMode) {
      ctx.strokeStyle = 'rgba(68,138,255,0.3)'
      ctx.lineWidth   = 0.5
      ctx.strokeRect(x - 1, y - 1, maxW + 2, limited.length * fsPx * 1.2 + 2)
    }
  } else {
    const { anchorX, ctxAlign } = resolveTextAnchor(el, x, zoom)
    ctx.textAlign = ctxAlign
    const hardLines = _splitHardLines(text)
    let naturalWidthPx = 0

    if (hardLines.length <= 1) {
      ctx.fillText(text, anchorX, y)

      const metrics  = ctx.measureText(text)
      const cacheKey = `${ctx.font}|${text}`
      const wMm      = metrics.width / zoom / mmToPx(1)
      naturalWidthPx = metrics.width
      TEXT_WIDTH_CACHE.set(cacheKey, wMm)
      if (el.tempId && !n(el.elementWidth, 0)) {
        TEXT_WIDTH_CACHE.set(`id|${el.tempId}`, wMm)
      }
    } else {
      hardLines.forEach((line, i) => ctx.fillText(line, anchorX, y + i * fsPx * 1.2))

      let widestPx = 0
      for (const line of hardLines) {
        const w = ctx.measureText(line).width
        if (w > widestPx) widestPx = w
      }
      naturalWidthPx = widestPx
      const wMm = widestPx / zoom / mmToPx(1)
      if (el.tempId && !n(el.elementWidth, 0)) {
        TEXT_WIDTH_CACHE.set(`id|${el.tempId}`, wMm)
      }
    }
    if (!previewMode) {
      // naturalWidthPx replaces a direct `metrics.width` read here.
      // `metrics` was declared with const inside the single-line branch
      // above, so this line threw a ReferenceError whenever an element
      // had hard line breaks AND no elementWidth — the multi-line
      // branch never defined it. Hoisting the measurement into a
      // variable both branches assign fixes the crash.
      const boxW = n(el.elementWidth, 0)
        ? mmToPx(n(el.elementWidth, 0)) * zoom
        : naturalWidthPx
      ctx.strokeStyle = 'rgba(68,138,255,0.3)'
      ctx.lineWidth   = 0.5
      ctx.strokeRect(x - 1, y - 1, boxW + 2, fsPx + 2)
    }
  }
}

function fillMultiLineText(ctx, text, x, y, fsPx) {
  const normalised = String(text).replace(/\r\n?/g, '\n')
  const hardLines  = normalised.split('\n')
  let widestPx = 0
  hardLines.forEach((line, i) => {
    ctx.fillText(line, x, y + i * fsPx * 1.2)
    const w = ctx.measureText(line).width
    if (w > widestPx) widestPx = w
  })
  return { widthPx: widestPx, lineCount: hardLines.length }
}

function drawTokenized(ctx, el, x, y, zoom, previewMode, previewData, label, locale) {
  const fsPx   = mmToPx(n(el.fontSize, 3)) * zoom
  const weight = el.fontBold   ? 'bold'   : 'normal'
  const style  = el.fontItalic ? 'italic' : 'normal'
  const family = cssFamilyFor(el.font_key || _defaultFontKey)
  const font   = `${style} ${weight} ${fsPx}px ${family}`
  const valueText = resolveValueText(el, previewMode, previewData, locale)
  const showLabel = shouldShowLabel(el)

  ctx.font         = font
  ctx.fillStyle    = '#000000'
  ctx.textBaseline = 'top'

  if (!showLabel) {
    const { anchorX, ctxAlign } = resolveTextAnchor(el, x, zoom)
    ctx.textAlign = ctxAlign

    // Word wrap, same gate as drawSimpleText: wordWrap + a real
    // elementWidth. Without this a database element (e.g. the
    // description token) drew as one long line no matter what the
    // properties panel said — the wrap branch only existed on the
    // simple-text path, so it was unreachable for tokenized types.
    if (el.wordWrap && n(el.elementWidth, 0) > 0) {
      const maxW     = mmToPx(n(el.elementWidth, 0)) * zoom
      const maxLines = n(el.maxLines, 99)
      const lines    = _wrapText(ctx, valueText, maxW)
      const limited  = lines.slice(0, maxLines)

      limited.forEach((line, i) => ctx.fillText(line, anchorX, y + i * fsPx * 1.2))

      if (el.tempId) TEXT_WIDTH_CACHE.set(`id|${el.tempId}`, n(el.elementWidth, 0))
      if (!previewMode) {
        ctx.strokeStyle = 'rgba(68,138,255,0.3)'
        ctx.lineWidth   = 0.5
        ctx.strokeRect(x - 1, y - 1, maxW + 2, limited.length * fsPx * 1.2 + 2)
      }
      return
    }

    const { widthPx } = fillMultiLineText(ctx, valueText, anchorX, y, fsPx)
    cacheWidth(ctx, el, valueText, zoom)
    drawHintBox(ctx, el, previewMode, x, y, widthPx, fsPx)
    return
  }

  const labelText = el.fieldLabel ? String(el.fieldLabel) : ''
  const separator = el.labelSeparator || ''
  const labelWithSep = labelText + separator

  ctx.textAlign = 'left'
  ctx.fillText(labelWithSep, x, y)

  if (!valueText) {
    cacheWidth(ctx, el, labelWithSep, zoom)
    drawHintBox(ctx, el, previewMode, x, y, ctx.measureText(labelWithSep).width, fsPx)
    return
  }

  const valueFsPx = el.valueFontSize
    ? mmToPx(n(el.valueFontSize, el.fontSize)) * zoom
    : fsPx
  const valueFont = `${style} ${weight} ${valueFsPx}px ${family}`
  ctx.font = valueFont

  let valueX, valueY, valueWidthPx
  // Either coordinate is enough to mean "position the value
  // explicitly". The gate used to be `valueX != null` alone, so setting
  // only Y did nothing at all — the value stayed right-aligned at the
  // label edge and the user's number was silently ignored. The X
  // fallback below already handles a missing X; the gate just never let
  // it run.
  if (el.valueX != null || el.valueY != null) {
    valueX = mmToPx(n(el.valueX ?? el.labelX, 0)) * zoom
    valueY = mmToPx(n(el.valueY ?? el.labelY, 0)) * zoom
    ctx.textAlign = 'left'
    const m = fillMultiLineText(ctx, valueText, valueX, valueY, valueFsPx)
    valueWidthPx = m.widthPx
  } else {
    const labelWidthMm = n(label?.labelWidth, 0)
    const marginMm     = n(el.labelX, 0)
    const rightEdgePx  = mmToPx(labelWidthMm - marginMm) * zoom
    valueY = y
    ctx.textAlign = 'right'
    const normalised = String(valueText).replace(/\r\n?/g, '\n')
    const hardLines  = normalised.split('\n')
    let widestPx = 0
    hardLines.forEach((line, i) => {
      ctx.fillText(line, rightEdgePx, valueY + i * valueFsPx * 1.2)
      const w = ctx.measureText(line).width
      if (w > widestPx) widestPx = w
    })
    valueWidthPx = widestPx
    valueX = rightEdgePx - widestPx
  }

  const totalWidthPx = (valueX + valueWidthPx) - x
  const totalWidthMm = totalWidthPx / zoom / mmToPx(1)
  if (el.tempId) TEXT_WIDTH_CACHE.set(`id|${el.tempId}`, totalWidthMm)

  if (!previewMode) {
    ctx.strokeStyle = 'rgba(68,138,255,0.3)'
    ctx.lineWidth   = 0.5
    ctx.strokeRect(x - 1, y - 1, totalWidthPx + 2, Math.max(fsPx, valueFsPx) + 2)
  }
}

function cacheWidth(ctx, el, text, zoom) {
  const metrics = ctx.measureText(text)
  const wMm     = metrics.width / zoom / mmToPx(1)
  const cacheKey = `${ctx.font}|${text}`
  TEXT_WIDTH_CACHE.set(cacheKey, wMm)
  if (el.tempId && !n(el.elementWidth, 0)) {
    TEXT_WIDTH_CACHE.set(`id|${el.tempId}`, wMm)
  }
}

function drawHintBox(ctx, el, previewMode, x, y, widthPx, fsPx) {
  if (previewMode) return
  ctx.strokeStyle = 'rgba(68,138,255,0.3)'
  ctx.lineWidth   = 0.5
  ctx.strokeRect(x - 1, y - 1, widthPx + 2, fsPx + 2)
}

function resolveBarcodeValue(el, previewMode, previewData) {
  let token = el.barcodeDataField || el.qrDataField || el.databaseField
  token = normalizeToken(token)
  if (token === 'custom' || el.barcodeDataField === 'custom') {
    const raw = el.customData || ''
    if (!previewMode) return raw || '{custom}'
    return raw.replace(/\{(\w+)\}/g, (_, k) => previewData[normalizeToken(k)] ?? `{${k}}`)
  }
  if (previewMode) {
    return token ? (previewData[token] ?? `{${token}}`) : '{value}'
  }
  return token ? `{${token}}` : '{value}'
}

async function drawBarcode(ctx, el, x, y, zoom, previewMode, previewData) {
  const value = resolveBarcodeValue(el, previewMode, previewData)
  const wMm   = n(el.barcodeWidth,  n(el.elementWidth,  30))
  const hMm   = n(el.barcodeHeight, n(el.elementHeight, 10))
  const w     = mmToPx(wMm) * zoom
  const h     = mmToPx(hMm) * zoom

  try {
    const imgData = await renderBarcode(value, el.barcodeType || 'code128', {
      width: Math.round(w), height: Math.round(h),
      displayValue: !!el.barcodeDisplayValue,
    })
    const img = await loadImage(imgData)
    ctx.drawImage(img, x, y, w, h)
  } catch {
    drawPlaceholder(ctx, el, x, y, zoom, `[${el.barcodeType || 'barcode'}]`)
  }
}

async function drawQR(ctx, el, x, y, zoom, previewMode, previewData) {
  const value  = resolveBarcodeValue(el, previewMode, previewData)
  const sizeMm = n(el.qrSize, n(el.elementWidth, n(el.elementHeight, 15)))
  const size   = mmToPx(sizeMm) * zoom

  try {
    const imgData = await renderQR(value, Math.round(size))
    const img     = await loadImage(imgData)
    ctx.drawImage(img, x, y, size, size)
  } catch {
    drawPlaceholder(ctx, el, x, y, zoom, '[QR]')
  }
}

const IMAGE_CACHE = new Map()

// Logo / inline image rendering.
//
// FIXED 2026-06-14: priority order corrected. Previously the upload-time
// parsed.widthMm/heightMm won over el.elementWidth/elementHeight, which
// meant the selection handles (which use getElementWidth → reads
// elementWidth) updated correctly on drag-resize, but the rendered
// image stayed locked at the original upload dimensions. The bug
// looked like "the logo just moves, the size doesn't change" — the
// bounding box and handles followed the resize but the image inside
// did not.
//
// elementWidth is now the source of truth. parsed.widthMm is kept as a
// fallback for legacy elements where elementWidth was never populated
// (very old rows, or elements built from a flow that didn't set the
// dimensions). The hardcoded 15/10mm at the tail is the last-resort
// fallback for elements with no size data at all.
async function drawImage(ctx, el, x, y, zoom) {
  if (!el.imageData) { drawPlaceholder(ctx, el, x, y, zoom, '[logo]'); return }

  try {
    let parsed = null
    try { parsed = JSON.parse(el.imageData) } catch {}

    const rawSrc = parsed?.previewUrl || el.imageData
    const src    = resolveImageSrc(rawSrc)

    // elementWidth/elementHeight come first — that's what the user
    // drags. parsed.widthMm/heightMm is the upload-time fallback for
    // elements that were placed before the resize-aware path existed.
    const wMm = n(el.elementWidth,  n(parsed?.widthMm,  15))
    const hMm = n(el.elementHeight, n(parsed?.heightMm, 10))
    const w   = mmToPx(wMm) * zoom
    const h   = mmToPx(hMm) * zoom

    const img = await loadImage(src)
    ctx.drawImage(img, x, y, w, h)
  } catch {
    drawPlaceholder(ctx, el, x, y, zoom, '[logo]')
  }
}

function drawDivider(ctx, el, x, y, zoom) {
  const w  = mmToPx(n(el.lineWidth, 50)) * zoom
  const th = mmToPx(n(el.lineThickness, 0.3)) * zoom
  ctx.beginPath()
  ctx.moveTo(x, y); ctx.lineTo(x + w, y)
  ctx.strokeStyle = '#000000'
  ctx.lineWidth   = Math.max(0.5, th)
  ctx.stroke()
}

function drawPlaceholder(ctx, el, x, y, zoom, label = null) {
  const w    = mmToPx(getElementWidth(el, ctx, zoom)) * zoom
  const h    = mmToPx(getElementHeight(el)) * zoom
  const text = label || el.fieldType || '?'

  ctx.strokeStyle = 'rgba(255,100,100,0.5)'
  ctx.lineWidth   = 1
  ctx.setLineDash([3, 3])
  ctx.strokeRect(x, y, w, h)
  ctx.setLineDash([])
  ctx.fillStyle    = 'rgba(255,100,100,0.7)'
  ctx.font         = `${Math.max(8, 10 * zoom)}px Arial`
  ctx.textBaseline = 'middle'
  ctx.textAlign    = 'left'
  ctx.fillText(text, x + 2, y + h / 2)
}

export function drawSelectionHandles(ctx, el, zoom) {
  const baseX = mmToPx(n(el.labelX, 0)) * zoom
  const baseY = mmToPx(n(el.labelY, 0)) * zoom
  const rot   = n(el.rotation, 0)
  const wMm   = getElementWidth(el, ctx, zoom)
  const hMm   = getElementHeight(el)
  const wPx   = mmToPx(wMm) * zoom
  const hPx   = mmToPx(hMm) * zoom
  const hs    = 7

  let x = baseX, y = baseY, bw = wPx, bh = hPx
  if (rot === 90)  { x = baseX - hPx; bw = hPx; bh = wPx }
  if (rot === 180) { x = baseX - wPx; y = baseY - hPx }
  if (rot === 270) { y = baseY - wPx; bw = hPx; bh = wPx }

  ctx.strokeStyle = '#448AFF'
  ctx.lineWidth   = 1.5
  ctx.setLineDash([4, 2])
  ctx.strokeRect(x - 2, y - 2, bw + 4, bh + 4)
  ctx.setLineDash([])

  getHandlePositions(x, y, bw, bh).forEach(([hx, hy, corner]) => {
    ctx.fillStyle   = corner === 'lock' ? '#FFD740' : '#ffffff'
    ctx.strokeStyle = '#448AFF'
    ctx.lineWidth   = 1.5
    ctx.beginPath()
    if (corner === 'lock') ctx.arc(hx, hy, hs / 2, 0, Math.PI * 2)
    else ctx.rect(hx - hs / 2, hy - hs / 2, hs, hs)
    ctx.fill(); ctx.stroke()

    if (corner === 'lock') {
      ctx.fillStyle = el.locked ? '#F44336' : '#4CAF50'
      ctx.beginPath()
      ctx.arc(hx, hy, hs / 2 - 1.5, 0, Math.PI * 2)
      ctx.fill()
    }
  })
}

export function getHandlePositions(x, y, w, h) {
  return [
    [x,         y,         'tl'],
    [x + w / 2, y,         'tm'],
    [x + w,     y,         'tr'],
    [x,         y + h / 2, 'ml'],
    [x + w,     y + h / 2, 'mr'],
    [x,         y + h,     'bl'],
    [x + w / 2, y + h,     'bm'],
    [x + w,     y + h,     'br'],
    [x,         y,         'lock'],
  ]
}

export function hitTestHandle(el, zoom, cx, cy) {
  const x  = mmToPx(n(el.labelX, 0)) * zoom
  const y  = mmToPx(n(el.labelY, 0)) * zoom
  const { w, h } = getEffectiveBounds(el, null, zoom)
  const wPx = mmToPx(w) * zoom
  const hPx = mmToPx(h) * zoom
  const hs  = 8

  for (const [hx, hy, role] of getHandlePositions(x, y, wPx, hPx)) {
    if (Math.abs(cx - hx) <= hs && Math.abs(cy - hy) <= hs) return role
  }
  return null
}

export function hitTestElement(el, zoom, cx, cy) {
  const baseX = mmToPx(n(el.labelX, 0)) * zoom
  const baseY = mmToPx(n(el.labelY, 0)) * zoom
  const rot   = n(el.rotation, 0)
  const wMm   = getElementWidth(el, null, zoom)
  const hMm   = getElementHeight(el)
  const wPx   = mmToPx(wMm) * zoom
  const hPx   = mmToPx(hMm) * zoom

  let x = baseX, y = baseY, bw = wPx, bh = hPx
  if (rot === 90)  { x = baseX - hPx; bw = hPx; bh = wPx }
  if (rot === 180) { x = baseX - wPx; y = baseY - hPx }
  if (rot === 270) { y = baseY - wPx; bw = hPx; bh = wPx }

  return cx >= x && cx <= x + bw && cy >= y && cy <= y + bh
}

function loadImage(src) {
  if (IMAGE_CACHE.has(src)) return Promise.resolve(IMAGE_CACHE.get(src))
  return new Promise((res, rej) => {
    const img   = new Image()
    img.onload  = () => { IMAGE_CACHE.set(src, img); res(img) }
    img.onerror = rej
    img.src     = src
  })
}